import React, { useState } from "react";
import BaseModal from "../modals/BaseModal";
import Button from "../forms/Button";
import Input from "../forms/Input";
import { sprintService, apiErrorMessage } from "../../api/taskBoardService";
import { PRIORITIES } from "./constants";
import { PriorityBadge, formatDate } from "./shared";

const fieldClass = "input-field w-full";

const Label = ({ text, error, children, optional }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-sm font-medium text-gray-700 dark:text-light-200">
      {text}
      {optional ? <span className="text-gray-400 ml-1 font-normal">(optional)</span> : <span className="text-red-500 ml-1">*</span>}
    </span>
    {children}
    {error && <span className="text-sm text-red-500">{error}</span>}
  </label>
);

/**
 * "Create Sprint" from the tasks selected in Product Backlog. The API creates
 * the sprint and moves the tasks to Sprint Backlog in one transaction, so
 * nothing is created or moved if any task is not ready. Tasks keep their
 * description, priority and due date; a priority / due date can be filled in
 * here only where the task has none. No assignee, squad or complexity.
 */
const CreateSprintModal = ({ tasks, onClose, onCreated }) => {
  const [form, setForm] = useState({ name: "", description: "", goal: "", startDate: "", endDate: "" });
  const [fill, setFill] = useState({}); // taskId -> { priority, dueDate }
  const [errors, setErrors] = useState({});
  const [taskErrors, setTaskErrors] = useState({}); // taskId -> message (from the API)
  const [serverError, setServerError] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };
  const setFillValue = (id, key, value) => {
    setFill((f) => ({ ...f, [id]: { ...f[id], [key]: value } }));
    setTaskErrors((er) => ({ ...er, [id]: undefined }));
  };

  const priorityOf = (t) => t.priority || fill[t._id]?.priority || "";
  const dueOf = (t) => (t.dueDate ? "set" : fill[t._id]?.dueDate || "");

  const submit = async (e) => {
    e?.preventDefault();
    const er = {};
    if (!form.name.trim()) er.name = "Sprint name is required";
    if (!form.goal.trim()) er.goal = "Sprint goal is required";
    if (!form.startDate) er.startDate = "Start date is required";
    if (!form.endDate) er.endDate = "End date is required";
    else if (form.startDate && form.endDate < form.startDate) er.endDate = "End date cannot be earlier than start date";
    const te = {};
    tasks.forEach((t) => {
      const missing = [!priorityOf(t) && "priority", !dueOf(t) && "due date"].filter(Boolean);
      if (missing.length) te[t._id] = `Add ${missing.join(" and ")}`;
    });
    setErrors(er);
    setTaskErrors(te);
    if (Object.keys(er).length || Object.keys(te).length) return;

    setSaving(true);
    setServerError("");
    try {
      const res = await sprintService.createFromBacklog({
        name: form.name.trim(),
        description: form.description.trim(),
        goal: form.goal.trim(),
        startDate: new Date(form.startDate).toISOString(),
        endDate: new Date(form.endDate).toISOString(),
        tasks: tasks.map((t) => ({
          taskId: t._id,
          ...(t.priority ? {} : { priority: fill[t._id].priority }),
          ...(t.dueDate ? {} : { dueDate: new Date(fill[t._id].dueDate).toISOString() }),
        })),
      });
      onCreated(res.data);
    } catch (err) {
      const data = err?.response?.data;
      if (data?.code === "SPRINT_TASKS_INVALID") {
        const map = {};
        (data.invalid || []).forEach((i) => {
          map[i.taskId] = i.message || `Missing ${(i.missing || []).join(", ")}`;
        });
        setTaskErrors(map);
        setServerError("Some tasks are not ready. Nothing was created.");
      } else if (data?.code === "DUPLICATE_NAME") {
        setErrors({ name: "A sprint with this name already exists" });
      } else {
        setServerError(apiErrorMessage(err, "Could not create the sprint"));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseModal
      isOpen
      onClose={saving ? () => {} : onClose}
      size="lg"
      title="Create sprint"
      subtitle={`${tasks.length} task${tasks.length === 1 ? "" : "s"} from the Product Backlog will move to the Sprint Backlog.`}
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            {saving ? "Creating…" : "Create sprint"}
          </Button>
        </div>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate>
        {serverError && (
          <div role="alert" className="text-sm p-3 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
            {serverError}
          </div>
        )}
        <Input label="Sprint name" required autoFocus maxLength={100} value={form.name} onChange={set("name")} error={errors.name} />
        <Label text="Goal" error={errors.goal}>
          <textarea className={`${fieldClass} min-h-[64px]`} maxLength={1000} value={form.goal} onChange={set("goal")} />
        </Label>
        <Label text="Description" optional>
          <textarea className={`${fieldClass} min-h-[64px]`} maxLength={10000} value={form.description} onChange={set("description")} />
        </Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Start date" type="date" required value={form.startDate} onChange={set("startDate")} error={errors.startDate} />
          <Input label="End date" type="date" required value={form.endDate} min={form.startDate || undefined} onChange={set("endDate")} error={errors.endDate} />
        </div>

        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-light-200 mb-2">Tasks</p>
          <ul className="divide-y divide-line border border-line rounded-xl">
            {tasks.map((t) => (
              <li key={t._id} className="p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="font-mono text-[11px] text-faint pt-0.5">#{t.number}</span>
                  <span className="flex-1 min-w-0 text-sm text-ink break-words">{t.title}</span>
                  {t.priority && <PriorityBadge priority={t.priority} />}
                  {t.dueDate && <span className="text-xs text-faint shrink-0">Due {formatDate(t.dueDate)}</span>}
                </div>
                {(!t.priority || !t.dueDate) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {!t.priority && (
                      <select
                        aria-label={`Priority for task #${t.number}`}
                        className={fieldClass}
                        value={fill[t._id]?.priority || ""}
                        onChange={(e) => setFillValue(t._id, "priority", e.target.value)}
                      >
                        <option value="">Priority…</option>
                        {PRIORITIES.map((p) => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    )}
                    {!t.dueDate && (
                      <input
                        type="date"
                        aria-label={`Due date for task #${t.number}`}
                        className={fieldClass}
                        value={fill[t._id]?.dueDate || ""}
                        onChange={(e) => setFillValue(t._id, "dueDate", e.target.value)}
                      />
                    )}
                  </div>
                )}
                {taskErrors[t._id] && <p className="text-sm text-red-500">{taskErrors[t._id]}</p>}
              </li>
            ))}
          </ul>
        </div>
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </BaseModal>
  );
};

export default CreateSprintModal;
